import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const ivoryTower: TemplateRegistration = {
  meta: {
    id: 'ivory-tower',
    name: 'Ivory Tower',
    category: 'Classic',
    description: 'Elegant ivory background with refined serif typography',
    previewImage: '',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Cormorant Garamond', 'Lato']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-ivory">
        <div class="container">
          <div class="ornamental-divider"></div>
          <h2 class="section-title">About Us</h2>
          <p class="about-text">${safeAbout}</p>
          <div class="ornamental-divider"></div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceItems = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const num = String(i + 1).padStart(2, '0');
        return `
          <div class="service-item">
            <span class="service-num">${num}</span>
            <div class="service-body">
              <h3>${sName}</h3>
              <p>${sDesc}</p>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-list">${serviceItems}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-ivory">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">What Our Clients Say</h2>
          <div class="testimonials-list">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-ivory">
        <div class="container">
          <h2 class="section-title">Frequently Asked Questions</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-ivory">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-wrapper">
            ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-card-item"><h4>Phone</h4><div class="contact-info"><div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div></div></div>` : ''}
            ${enabledFeatures.has('address-map') && address ? `<div class="contact-card-item"><h4>Address</h4><div class="contact-info"><div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Maps</a>` : ''}</div></div>` : ''}
            <div class="contact-card-item" style="grid-column: ${enabledFeatures.has('phone-display') && phone && enabledFeatures.has('address-map') && address ? 'span 1' : '1 / -1'};">
              <h4>Message Us</h4>
              <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
                <input type="text" placeholder="Your Name" required>
                <input type="email" placeholder="Your Email" required>
                <textarea placeholder="Message" rows="4" required></textarea>
                <button type="submit">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #800020; --bg: #FFFFF0; --bg-alt: #F5F0E0; --accent: #800020;
      --text: #2C1810; --text-muted: #6B5B4F;
      --font-heading: 'Cormorant Garamond', serif; --font-body: 'Lato', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; }
    .container { max-width: 800px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(255,255,240,.95); backdrop-filter: blur(8px); border-bottom: 3px double var(--accent); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 800px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.6rem; color: var(--text); font-weight: 700; font-style: italic; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 400; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--accent); margin: 5px 0; transition: .3s; }

    /* HERO */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; justify-content: center; overflow: hidden; padding: 80px 48px; }
    .hero-frame { border: 3px solid var(--accent); padding: 64px 56px; max-width: 720px; width: 100%; text-align: center; position: relative; z-index: 2; background: rgba(255,255,240,.85); }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.5rem, 6vw, 4.5rem); line-height: 1.15; font-weight: 700; font-style: italic; margin-bottom: 20px; color: var(--text); }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.15rem); color: var(--text-muted); max-width: 600px; margin: 0 auto 36px; }
    .hero-cta { display: inline-block; padding: 14px 40px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1.05rem; font-style: italic; letter-spacing: 1px; border: none; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(128,0,32,.25); opacity: 1; color: var(--bg); }

    /* ORNAMENTAL */
    .ornamental-divider { width: 120px; height: 2px; margin: 24px auto; position: relative; background: var(--accent); }
    .ornamental-divider::before, .ornamental-divider::after { content: ''; position: absolute; top: -3px; width: 8px; height: 8px; border: 2px solid var(--accent); border-radius: 50%; background: var(--bg); }
    .ornamental-divider::before { left: -4px; }
    .ornamental-divider::after { right: -4px; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-ivory { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.8rem); font-weight: 700; font-style: italic; text-align: center; margin-bottom: 48px; color: var(--text); }

    /* ABOUT */
    .about-text { font-size: 1.1rem; color: var(--text-muted); line-height: 1.9; text-align: left; max-width: 700px; margin: 0 auto; }
    .about-text::first-letter { font-family: var(--font-heading); font-size: 3.5rem; float: left; line-height: 1; margin-right: 12px; margin-top: 4px; color: var(--accent); font-weight: 700; }

    /* SERVICES */
    .services-list { display: flex; flex-direction: column; gap: 0; counter-reset: service-counter; }
    .service-item { display: flex; align-items: flex-start; gap: 20px; padding: 28px 0; border-bottom: 1px solid rgba(128,0,32,.15); counter-increment: service-counter; }
    .service-item:last-child { border-bottom: none; }
    .service-num { font-family: var(--font-heading); font-size: 2.5rem; font-weight: 700; color: rgba(128,0,32,.15); line-height: 1; flex-shrink: 0; min-width: 48px; }
    .service-icon { display: none; }
    .icon-char { font-size: 22px; line-height: 1; }
    .service-body h3 { font-family: var(--font-heading); font-size: 1.4rem; font-weight: 700; font-style: italic; margin-bottom: 6px; }
    .service-body p { color: var(--text-muted); font-size: .95rem; }
    .service-price { display: inline-block; margin-top: 10px; color: var(--accent); font-weight: 700; font-size: 1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border: 3px double var(--accent); }

    /* TESTIMONIALS */
    .testimonials-list { display: flex; flex-direction: column; gap: 48px; max-width: 700px; margin: 0 auto; }
    .testimonial-card { text-align: center; padding: 32px 24px; position: relative; }
    .testimonial-card::before { content: '\\201C'; font-family: var(--font-heading); font-size: 6rem; color: rgba(128,0,32,.12); position: absolute; top: -20px; left: 50%; transform: translateX(-50%); line-height: 1; }
    .testimonial-body { padding-top: 40px; }
    .testimonial-body p { font-family: var(--font-heading); font-size: 1.2rem; font-style: italic; color: var(--text-muted); line-height: 1.8; margin-bottom: 20px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #C4B09E; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 700px; margin: 0 auto; }
    .faq-item { border-bottom: 1px solid rgba(128,0,32,.15); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.2rem; font-style: italic; margin-bottom: 10px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 700px; margin: 0 auto; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid rgba(128,0,32,.15); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.15rem; font-style: italic; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--accent); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; margin: 0 auto; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid rgba(128,0,32,.15); color: var(--text-muted); font-size: .95rem; text-align: center; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: var(--bg); font-size: 1.2rem; }
    .review-text { color: var(--bg); font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: var(--bg-alt); padding: 20px 0; border-top: 3px double var(--accent); border-bottom: 3px double var(--accent); }
    .promo-inner { display: flex; align-items: center; justify-content: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: var(--bg); font-family: var(--font-heading); padding: 8px 20px; font-size: 1.1rem; font-style: italic; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT */
    .contact-wrapper { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 800px; margin: 0 auto; }
    .contact-card-item { text-align: center; padding: 32px 20px; border: 1px solid rgba(128,0,32,.15); }
    .contact-card-item h4 { font-family: var(--font-heading); font-style: italic; margin-bottom: 12px; color: var(--accent); }
    .contact-info { display: flex; flex-direction: column; gap: 16px; align-items: center; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: var(--bg); border: 1px solid rgba(128,0,32,.3); color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 16px; background: var(--accent); color: var(--bg); font-family: var(--font-heading); font-size: 1.05rem; font-style: italic; letter-spacing: 1px; border: none; cursor: pointer; transition: transform .2s; }
    .contact-form button:hover { transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--bg-alt); border-top: 3px double var(--accent); padding: 40px 0; text-align: center; }
    .footer p { color: var(--text-muted); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; }
      .nav-links.show { display: flex; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(255,255,240,.98); padding: 16px 24px; gap: 12px; border-bottom: 3px double var(--accent); }
      .nav-toggle { display: block; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .price-row { flex-direction: column; gap: 8px; }
      .contact-wrapper { grid-template-columns: 1fr; }
      .contact-card-item { grid-column: span 1 !important; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero { padding: 80px 20px; }
      .hero-frame { padding: 40px 24px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-frame">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get In Touch</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
